import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

export default function MetaCreativeTest() {
  const [dateRange, setDateRange] = useState('last30');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [detailsMap, setDetailsMap] = useState({});
  const [filterCreativeIds, setFilterCreativeIds] = useState('');
  const [adMapRows, setAdMapRows] = useState([]);
  const [adMapPivot, setAdMapPivot] = useState([]);
  const [creativeDetailsRows, setCreativeDetailsRows] = useState([]);
  const [previewRows, setPreviewRows] = useState([]);

  const brandId = useMemo(() => {
    try { return localStorage.getItem('currentBrandId') || ''; } catch { return ''; }
  }, []);

  const fetchNow = async () => {
    if (!brandId) {
      console.error('No brand selected');
      return;
    }
    setLoading(true);
    try {
      const qs = (() => {
        if (dateRange === 'custom') {
          if (!customStartDate || !customEndDate) return '';
          const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          return `?dateRange=custom&startDate=${fmt(customStartDate)}&endDate=${fmt(customEndDate)}`;
        }
        return `?dateRange=${dateRange}`;
      })();

      const perfResp = await axios.get(`https://api.glowytics.com/api/meta-ads/live/creative-insights${qs}`, {
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId }
      });
      const perfRows = Array.isArray(perfResp?.data?.creatives) ? perfResp.data.creatives : [];

      // Optional filter by creativeIds comma-separated
      let filtered = perfRows;
      const wanted = String(filterCreativeIds || '').trim();
      if (wanted) {
        const set = new Set(wanted.split(',').map(s => s.trim()).filter(Boolean));
        filtered = perfRows.filter(r => r.creative_id && set.has(r.creative_id));
      }

      setRows(filtered);
    } catch (e) {
      console.error('Meta creative test fetch error:', e);
      setRows([]);
      setDetailsMap({});
    } finally {
      setLoading(false);
    }
  };

  const fetchPreviews = async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      // take first 10 creative ids from details (fallback to adMapRows)
      const fromDetails = creativeDetailsRows.map(d => d.creative_id).filter(Boolean);
      const pool = fromDetails.length ? fromDetails : adMapRows.map(r => r.creative_id).filter(Boolean);
      const ids = Array.from(new Set(pool)).slice(0, 10);
      if (ids.length === 0) { setPreviewRows([]); setLoading(false); return; }
      const q = encodeURIComponent(ids.join(','));
      const resp = await axios.get(`https://api.glowytics.com/api/meta-ads/live/creative-preview?creativeIds=${q}&adFormat=MOBILE_FEED_STANDARD`, {
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId }
      });
      const rows = Array.isArray(resp?.data?.rows) ? resp.data.rows : [];
      setPreviewRows(rows);
    } catch (e) {
      console.error('fetchPreviews error', e);
      setPreviewRows([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdCreativeMap = async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      // Fetch for a specific date: 2025-11-23
      const resp = await axios.get(`https://api.glowytics.com/api/meta-ads/live/ad-creative-map?date=2025-11-23`, {
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId }
      });
      const rows = Array.isArray(resp?.data?.rows) ? resp.data.rows : [];
      const pivot = Array.isArray(resp?.data?.pivot) ? resp.data.pivot : [];
      setAdMapRows(rows);
      setAdMapPivot(pivot);

      // Fetch creative details LIVE for the creative_ids in map rows
      const creativeIds = Array.from(new Set(rows.map(r => r.creative_id).filter(Boolean)));
      if (creativeIds.length > 0) {
        const q = encodeURIComponent(creativeIds.join(','));
        const det = await axios.get(`https://api.glowytics.com/api/meta-ads/live/creative-details?creativeIds=${q}`, {
          withCredentials: true,
          headers: { 'X-Brand-ID': brandId }
        });
        const drows = Array.isArray(det?.data?.rows) ? det.data.rows : [];
        setCreativeDetailsRows(drows);
      } else {
        setCreativeDetailsRows([]);
      }
    } catch (e) {
      console.error('fetchAdCreativeMap error', e);
      setAdMapRows([]);
      setAdMapPivot([]);
      setCreativeDetailsRows([]);
    } finally {
      setLoading(false);
    }
  };

  const aggregated = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      const key = r.creative_id || '(unknown)';
      if (!m[key]) {
        m[key] = { impressions: 0, clicks: 0, spend: 0, conversions: 0, revenue: 0, anyRow: r };
      }
      m[key].impressions += Number(r.impressions || 0);
      m[key].clicks += Number(r.clicks || 0);
      m[key].spend += Number(r.spend || r.cost || 0);
      m[key].conversions += Number(r.conversions || 0);
      m[key].revenue += Number(r.conversion_value || 0);
    });
    return m;
  }, [rows]);

  const list = useMemo(() => {
    return Object.entries(aggregated).map(([cid, agg]) => {
      const det = detailsMap[cid];
      return {
        creative_id: cid,
        name: det?.name || cid,
        object_type: det?.object_type || '',
        is_video: !!det?.is_video,
        image_url: det?.image_url || null,
        best_media_url: det?.best_media_url || det?.enhanced_image_url || det?.image_url || null,
        video_id: det?.video_id || null,
        video_watch_url: det?.video_watch_url || null,
        impressions: agg.impressions,
        clicks: agg.clicks,
        spend: agg.spend,
        conversions: agg.conversions,
        revenue: agg.revenue
      };
    }).sort((a, b) => Number(b.spend) - Number(a.spend));
  }, [aggregated, detailsMap]);

  // No auto-fetch on mount; user must click buttons to trigger requests

  return (
    <div className="p-6 space-y-4">
      <div className="bg-white p-4 rounded-xl shadow flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Date Range</label>
          <select
            className="px-3 py-2 border rounded-lg text-sm"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="last7">Last 7 days</option>
            <option value="last30">Last 30 days</option>
            <option value="last90">Last 90 days</option>
            <option value="thisMonth">This month</option>
            <option value="lastMonth">Last month</option>
            <option value="custom">Custom</option>
            <option value="all">All</option>
          </select>
        </div>
        {dateRange === 'custom' && (
          <div className="flex items-center gap-2">
            <DatePicker
              selected={customStartDate}
              onChange={(d) => setCustomStartDate(d)}
              selectsStart
              startDate={customStartDate}
              endDate={customEndDate}
              className="px-3 py-2 border rounded-lg text-sm"
              placeholderText="Start date"
              maxDate={new Date()}
            />
            <DatePicker
              selected={customEndDate}
              onChange={(d) => setCustomEndDate(d)}
              selectsEnd
              startDate={customStartDate}
              endDate={customEndDate}
              className="px-3 py-2 border rounded-lg text-sm"
              placeholderText="End date"
              maxDate={new Date()}
            />
          </div>
        )}
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-semibold text-gray-600 mb-1">Filter creative IDs (comma separated)</label>
          <input
            value={filterCreativeIds}
            onChange={(e) => setFilterCreativeIds(e.target.value)}
            placeholder="e.g. 123,456,789"
            className="w-full px-3 py-2 border rounded-lg text-sm"
          />
        </div>
        <button
          onClick={fetchNow}
          disabled={loading}
          className={`px-4 py-2 rounded-lg text-white ${loading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
        >
          {loading ? 'Fetching…' : 'Fetch'}
        </button>
      </div>

      <div className="bg-white p-4 rounded-xl shadow space-y-3 mt-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Ad Previews (first 10 creative IDs)</h2>
          <button
            onClick={fetchPreviews}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-white ${loading ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
          >
            {loading ? 'Fetching…' : 'Fetch Previews'}
          </button>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 pr-4">Creative ID</th>
                <th className="py-2 pr-4">Preview</th>
              </tr>
            </thead>
            <tbody>
              {previewRows.map((p) => (
                <tr key={p.creative_id} className="border-b">
                  <td className="py-2 pr-4 font-mono">{p.creative_id}</td>
                  <td className="py-2 pr-4">
                    {p.body ? (
                      <div
                        className="w-[300px] h-[240px] overflow-auto border rounded"
                        dangerouslySetInnerHTML={{ __html: p.body }}
                      />
                    ) : (
                      <span className="text-gray-500">{p.error || '-'}</span>
                    )}
                  </td>
                </tr>
              ))}
              {previewRows.length === 0 && !loading && (
                <tr><td className="py-6 text-center text-gray-500" colSpan={2}>No previews</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {/* Top creatives table removed per request */}

      <div className="bg-white p-4 rounded-xl shadow space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Ad → Creative Map (2025-11-23)</h2>
          <button
            onClick={fetchAdCreativeMap}
            disabled={loading}
            className={`px-4 py-2 rounded-lg text-white ${loading ? 'bg-gray-400' : 'bg-emerald-600 hover:bg-emerald-700'}`}
          >
            {loading ? 'Fetching…' : 'Fetch Map'}
          </button>
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 pr-4">Ad Name</th>
                <th className="py-2 pr-4">Ad ID</th>
                <th className="py-2 pr-4">Ad Set ID</th>
                <th className="py-2 pr-4">Ad Set Name</th>
                  <th className="py-2 pr-4">Campaign ID</th>
                  <th className="py-2 pr-4">Campaign Name</th>
                <th className="py-2 pr-4">Creative ID</th>
                <th className="py-2 pr-4">Impr.</th>
                <th className="py-2 pr-4">Clicks</th>
                <th className="py-2 pr-4">Spend</th>
              </tr>
            </thead>
            <tbody>
              {adMapRows.map((r) => (
                <tr key={r.ad_id} className="border-b">
                  <td className="py-2 pr-4">{r.ad_name || '-'}</td>
                  <td className="py-2 pr-4 font-mono">{r.ad_id}</td>
                  <td className="py-2 pr-4 font-mono">{r.adset_id || '-'}</td>
                  <td className="py-2 pr-4">{r.adset_name || '-'}</td>
                    <td className="py-2 pr-4 font-mono">{r.campaign_id || '-'}</td>
                    <td className="py-2 pr-4">{r.campaign_name || '-'}</td>
                  <td className="py-2 pr-4 font-mono">{r.creative_id || '-'}</td>
                  <td className="py-2 pr-4">{Number(r.impressions || 0).toLocaleString('tr-TR')}</td>
                  <td className="py-2 pr-4">{Number(r.clicks || 0).toLocaleString('tr-TR')}</td>
                  <td className="py-2 pr-4">{Math.round(Number(r.spend || 0)).toLocaleString('tr-TR')}</td>
                </tr>
              ))}
              {adMapRows.length === 0 && !loading && (
                <tr><td className="py-6 text-center text-gray-500" colSpan={6}>No data</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div>
          <h3 className="text-md font-semibold mt-4 mb-2">Pivot by Ad ID</h3>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left border-b">
                  <th className="py-2 pr-4">Ad ID</th>
                  <th className="py-2 pr-4">Ad Name</th>
                  <th className="py-2 pr-4">Ad Set ID</th>
                  <th className="py-2 pr-4">Ad Set Name</th>
                  <th className="py-2 pr-4">Campaign ID</th>
                  <th className="py-2 pr-4">Campaign Name</th>
                  <th className="py-2 pr-4">Creative IDs</th>
                  <th className="py-2 pr-4">Creatives Count</th>
                  <th className="py-2 pr-4">Total Impr.</th>
                </tr>
              </thead>
              <tbody>
                {adMapPivot.map((p) => (
                  <tr key={p.ad_id} className="border-b">
                    <td className="py-2 pr-4 font-mono">{p.ad_id}</td>
                    <td className="py-2 pr-4">{p.ad_name || '-'}</td>
                    <td className="py-2 pr-4 font-mono">{p.adset_id || '-'}</td>
                    <td className="py-2 pr-4">{p.adset_name || '-'}</td>
                    <td className="py-2 pr-4 font-mono">{p.campaign_id || '-'}</td>
                    <td className="py-2 pr-4">{p.campaign_name || '-'}</td>
                    <td className="py-2 pr-4 font-mono">{(p.creative_ids || []).join(', ')}</td>
                    <td className="py-2 pr-4">{p.creatives_count}</td>
                    <td className="py-2 pr-4">{Number(p.total_impressions || 0).toLocaleString('tr-TR')}</td>
                  </tr>
                ))}
                {adMapPivot.length === 0 && !loading && (
                  <tr><td className="py-6 text-center text-gray-500" colSpan={5}>No data</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="bg-white p-4 rounded-xl shadow space-y-3 mt-4">
        <h2 className="text-lg font-semibold">Creative Details (selected from map)</h2>
        <div className="text-sm text-gray-600 mb-2">Records: {creativeDetailsRows.length}</div>
        <div className="overflow-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left border-b">
                <th className="py-2 pr-4">Preview</th>
                <th className="py-2 pr-4">Creative ID</th>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Title</th>
                <th className="py-2 pr-4">Body</th>
                <th className="py-2 pr-4">thumbnail_url</th>
                <th className="py-2 pr-4">image_url</th>
                <th className="py-2 pr-4">video_id</th>
                <th className="py-2 pr-4">instagram_permalink_url</th>
                <th className="py-2 pr-4">image_hash</th>
                <th className="py-2 pr-4">image_crops</th>
                <th className="py-2 pr-4">effective_instagram_media_id</th>
                <th className="py-2 pr-4">creative_sourcing_spec</th>
                <th className="py-2 pr-4">video_permalink_url</th>
                <th className="py-2 pr-4">video_watch_url</th>
                <th className="py-2 pr-4">video_thumbnail_url</th>
                <th className="py-2 pr-4">video_source_url</th>
                <th className="py-2 pr-4">enhanced_image_url</th>
                <th className="py-2 pr-4">best_media_url</th>
              </tr>
            </thead>
            <tbody>
              {creativeDetailsRows.map((d) => {
                // Prefer proxy for thumbnail to avoid signature mismatch
                const thumbProxy = d.thumbnail_url && brandId
                  ? `https://api.glowytics.com/api/meta-ads/live/creative-thumbnail?creativeId=${encodeURIComponent(d.creative_id)}&brandId=${encodeURIComponent(brandId)}&w=512&h=512`
                  : null;
                // Prefer video thumbnail (reliable), then best/enhanced, then proxied creative thumbnail
                const mediaUrl = d.video_thumbnail_url || d.best_media_url || d.enhanced_image_url || thumbProxy || d.thumbnail_url || null;
                return (
                  <tr key={d.creative_id} className="border-b">
                    <td className="py-2 pr-4">
                      {mediaUrl ? (
                        <img
                          src={mediaUrl}
                          alt={d.name || d.creative_id}
                          className="w-32 h-32 object-cover rounded"
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                        />
                      ) : (
                        <span className="text-gray-400 text-xs">no preview</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 font-mono">{d.creative_id}</td>
                    <td className="py-2 pr-4">{d.name || '-'}</td>
                    <td className="py-2 pr-4">{d.title || '-'}</td>
                    <td className="py-2 pr-4">{d.body || '-'}</td>
                    <td className="py-2 pr-4 font-mono break-all">{d.thumbnail_url || '-'}</td>
                    <td className="py-2 pr-4 font-mono break-all">{d.image_url || '-'}</td>
                    <td className="py-2 pr-4 font-mono break-all">{d.video_id || '-'}</td>
                    <td className="py-2 pr-4 font-mono break-all">{d.instagram_permalink_url || '-'}</td>
                    <td className="py-2 pr-4 font-mono break-all">{d.image_hash || '-'}</td>
                    <td className="py-2 pr-4 font-mono break-all">{d.image_crops ? JSON.stringify(d.image_crops) : '-'}</td>
                    <td className="py-2 pr-4 font-mono break-all">{d.effective_instagram_media_id || '-'}</td>
                    <td className="py-2 pr-4 font-mono break-all">{d.creative_sourcing_spec ? JSON.stringify(d.creative_sourcing_spec) : '-'}</td>
                    <td className="py-2 pr-4">
                      {d.video_permalink_url ? <a href={d.video_permalink_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">link</a> : '-'}
                    </td>
                    <td className="py-2 pr-4">
                      {d.video_watch_url ? <a href={d.video_watch_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">link</a> : '-'}
                    </td>
                    <td className="py-2 pr-4">
                      {d.video_thumbnail_url ? <a href={d.video_thumbnail_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">link</a> : '-'}
                    </td>
                    <td className="py-2 pr-4">
                      {d.video_source_url ? <a href={d.video_source_url} target="_blank" rel="noreferrer" className="text-blue-600 underline">link</a> : '-'}
                    </td>
                    <td className="py-2 pr-4">
                      {d.enhanced_image_url ? <a href={d.enhanced_image_url} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">link</a> : '-'}
                    </td>
                    <td className="py-2 pr-4">
                      {d.best_media_url ? <a href={d.best_media_url} target="_blank" rel="noreferrer" className="text-blue-600 underline break-all">link</a> : '-'}
                    </td>
                  </tr>
                );
              })}
              {creativeDetailsRows.length === 0 && !loading && (
                <tr><td className="py-6 text-center text-gray-500" colSpan={11}>No data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
